import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleQuickLinks } from '@/mocks/articles';

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (path: string) => {
    setMobileOpen(false);
    setGuideOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExternal = (url: string) => {
    setMobileOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-secondary-200/50">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 no-underline flex-shrink-0">
              <span className="text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
                OOPBUY <span className="text-accent-500">Spreadsheet</span>
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-medium">
                Startseite
              </a>

              {/* Shopping Guide Dropdown */}
              <div ref={guideRef} className="relative">
                <button
                  onClick={() => setGuideOpen(!guideOpen)}
                  className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer font-medium"
                >
                  Shopping Guide
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`} />
                </button>
                {guideOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-secondary-200 rounded-xl overflow-hidden z-30 min-w-[220px] shadow-sm">
                    {articleQuickLinks.map((link) => (
                      <button
                        key={link.path}
                        onClick={() => handleNav(link.path)}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {link.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
              >
                Buy it now
              </a>
            </div>

            {/* Mobile: CTA + Hamburger */}
            <div className="flex md:hidden items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                Buy it now
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-9 h-9 flex items-center justify-center cursor-pointer"
                aria-label="Menü öffnen"
              >
                {mobileOpen ? (
                  <i className="ri-close-line text-xl text-foreground-950" />
                ) : (
                  <i className="ri-menu-line text-xl text-foreground-950" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="px-6 py-6 space-y-6">
              {/* Home */}
              <div>
                <a
                  href="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 py-3 text-base font-medium text-foreground-950"
                >
                  <i className="ri-home-4-line text-lg text-accent-500 w-5 h-5 flex items-center justify-center" />
                  Startseite
                </a>
              </div>

              {/* Shopping Guide Section */}
              <div>
                <h3 className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Shopping Guide
                </h3>
                <div className="space-y-1">
                  {articleQuickLinks.map((link) => (
                    <button
                      key={link.path}
                      onClick={() => handleNav(link.path)}
                      className="w-full text-left flex items-center gap-3 py-2.5 px-3 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <i className="ri-article-line text-accent-400 w-5 h-5 flex items-center justify-center flex-shrink-0" />
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories Section */}
              <div>
                <h3 className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Kategorien
                </h3>
                <div className="grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleExternal(cat.url)}
                      className="text-left px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-arrow-right-up-line text-accent-400 flex-shrink-0" />
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* External Link */}
              <div className="pt-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 bg-primary-500 text-white py-3 rounded-full text-sm font-medium"
                >
                  oopbuy spreadsheet
                  <i className="ri-external-link-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}